import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { normalisePhone } from '@shared/csv'
import { normalisePoll } from '@shared/polls'
import {
  api,
  ApiError,
  type GroupActionInput,
  type GroupBlueprint,
  type PollView,
} from '../lib/api'
import { ReadOnlyNotice } from './ReadOnlyNotice'

/**
 * WhatsApp groups.
 *
 * Everything here runs against the live socket rather than against our own database, so the
 * list is whatever WhatsApp says right now. That also means every action can be refused —
 * almost all of them require this number to be a group admin — and the refusal is shown in
 * WhatsApp's own words, because "not-authorized" tells an operator exactly what to fix and a
 * friendlier paraphrase would not.
 */
export function Groups() {
  const queryClient = useQueryClient()
  const [numberId, setNumberId] = useState<string | undefined>()
  const [open, setOpen] = useState<string | null>(null)
  const [insight, setInsight] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [bulkMode, setBulkMode] = useState(false)
  const [selectedJids, setSelectedJids] = useState<Set<string>>(new Set())
  const [bulkContact, setBulkContact] = useState('')
  const [bulkOutcome, setBulkOutcome] = useState<string | null>(null)

  const data = useQuery({
    queryKey: ['groups', numberId],
    queryFn: () => api.groups(numberId),
    retry: false,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['groups'] })

  const toggleJid = (jid: string) =>
    setSelectedJids((current) => {
      const next = new Set(current)
      if (next.has(jid)) next.delete(jid)
      else next.add(jid)
      return next
    })

  // One call per selected group, the same `groupAction` every single-group "Add people" flow
  // already uses — this just fans the same request out across a multi-select instead of a
  // dedicated bulk route, since each add is already an independent WhatsApp API call.
  const bulkAdd = useMutation({
    mutationFn: async (phone: string) => {
      const jids = [...selectedJids]
      const results = await Promise.all(
        jids.map((jid) =>
          api
            .groupAction(jid, { action: 'add', participants: [phone], numberId })
            .then(() => ({ jid, ok: true as const }))
            .catch((err) => ({ jid, ok: false as const, error: err instanceof ApiError ? err.message : 'Failed' })),
        ),
      )
      return results
    },
    onSuccess: (results) => {
      const failed = results.filter((r) => !r.ok)
      setBulkOutcome(
        failed.length === 0
          ? `Added to all ${results.length} groups.`
          : `Added to ${results.length - failed.length} of ${results.length} groups — refused: ${failed
              .map((f) => f.jid)
              .join(', ')}.`,
      )
      setBulkContact('')
      void refresh()
    },
  })

  if (data.isPending) return <div className="cad-card" style={{ height: '220px' }} />
  if (data.isError) {
    const message = data.error instanceof ApiError ? data.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { groups, numbers, canManage, error } = data.data!

  return (
    <>
      {!canManage && <ReadOnlyNotice what="WhatsApp groups" />}
      <header className="stack gap-1">
        <span className="cad-eyebrow">WhatsApp</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">Groups</h1>
          <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
            {numbers.length > 1 && (
              <select
                className="cad-input"
                aria-label="Which number"
                value={numberId ?? data.data!.numberId ?? ''}
                onChange={(e) => setNumberId(e.target.value)}
              >
                {numbers.map((number) => (
                  <option key={number.id} value={number.id}>
                    {number.label} · {number.phone}
                  </option>
                ))}
              </select>
            )}
            {canManage && groups.length > 0 && !error && (
              <button
                type="button"
                className="cad-btn"
                data-variant={bulkMode ? undefined : 'quiet'}
                onClick={() => {
                  setBulkMode((v) => !v)
                  setSelectedJids(new Set())
                  setBulkOutcome(null)
                }}
              >
                {bulkMode ? 'Done selecting' : 'Select groups'}
              </button>
            )}
            {canManage && !creating && groups.length >= 0 && !error && (
              <button type="button" className="cad-btn" onClick={() => setCreating(true)}>
                New group
              </button>
            )}
          </div>
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Read live from WhatsApp. Most changes need the connected number to be an admin of
          the group.
        </p>
      </header>

      {error && (
        <div className="cad-card">
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {error}
          </p>
        </div>
      )}

      <GroupHealth numberId={numberId} />

      {creating && canManage && (
        <CreateGroup
          numberId={numberId}
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            void refresh()
          }}
        />
      )}

      {!error && groups.length === 0 && (
        <div className="cad-card">
          <p className="cad-muted">This number is not in any groups.</p>
        </div>
      )}

      {bulkMode && (
        <div className="cad-card">
          <div className="stack gap-2">
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              {selectedJids.size} group{selectedJids.size === 1 ? '' : 's'} selected
            </span>
            <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
              <input
                className="cad-input"
                style={{ width: '200px' }}
                value={bulkContact}
                onChange={(e) => setBulkContact(e.target.value)}
                placeholder="+14155559001"
              />
              <button
                type="button"
                className="cad-btn"
                disabled={
                  selectedJids.size === 0 || !normalisePhone(bulkContact).phone || bulkAdd.isPending
                }
                onClick={() => bulkAdd.mutate(normalisePhone(bulkContact).phone!)}
              >
                {bulkAdd.isPending
                  ? 'Adding…'
                  : `Add to ${selectedJids.size || ''} group${selectedJids.size === 1 ? '' : 's'}`.trim()}
              </button>
            </div>
            {bulkOutcome && <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{bulkOutcome}</span>}
          </div>
        </div>
      )}

      <div className="stack gap-3">
        {groups.map((group) => (
          <section key={group.jid} className="cad-card">
            <div className="stack gap-3">
              <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
                <div className="row gap-2" style={{ alignItems: 'center' }}>
                  {bulkMode && (
                    <input
                      type="checkbox"
                      aria-label={`Select ${group.subject || 'Untitled group'}`}
                      checked={selectedJids.has(group.jid)}
                      onChange={() => toggleJid(group.jid)}
                    />
                  )}
                  <div className="stack gap-1">
                    <b>{group.subject || 'Untitled group'}</b>
                    <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                      {group.size} {group.size === 1 ? 'member' : 'members'} · {group.admins}{' '}
                      {group.admins === 1 ? 'admin' : 'admins'}
                    </span>
                  </div>
                </div>
                <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                  {group.announceOnly && <span className="cad-pill">admins only</span>}
                  {group.locked && <span className="cad-pill">details locked</span>}
                  <button
                    type="button"
                    className="cad-btn"
                    data-variant="quiet"
                    onClick={() => setInsight(insight === group.jid ? null : group.jid)}
                  >
                    {insight === group.jid ? 'Close' : 'Polls and activity'}
                  </button>
                  <button
                    type="button"
                    className="cad-btn"
                    data-variant="quiet"
                    onClick={() => setOpen(open === group.jid ? null : group.jid)}
                  >
                    {open === group.jid ? 'Close' : 'Manage'}
                  </button>
                </div>
              </div>

              {insight === group.jid && <GroupInsight jid={group.jid} numberId={numberId} />}

              {open === group.jid && (
                <GroupPanel jid={group.jid} numberId={numberId} onChanged={refresh} />
              )}
            </div>
          </section>
        ))}
      </div>
    </>
  )
}

function GroupPanel({
  jid,
  numberId,
  onChanged,
}: {
  jid: string
  numberId?: string
  onChanged: () => void
}) {
  const queryClient = useQueryClient()
  const [add, setAdd] = useState('')
  const [subject, setSubject] = useState<string | null>(null)
  const [link, setLink] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<string | null>(null)

  const detail = useQuery({
    queryKey: ['group', jid, numberId],
    queryFn: () => api.group(jid, numberId),
    retry: false,
  })

  const act = useMutation({
    mutationFn: (body: GroupActionInput) => api.groupAction(jid, { ...body, numberId }),
    onSuccess: (result, body) => {
      if (typeof result.link === 'string' || result.link === null) setLink(result.link)
      if (result.results) {
        // WhatsApp answers per participant: a number that has blocked group invites fails on
        // its own while the rest succeed, and saying "done" would be a lie.
        const failed = result.results.filter((r) => r.status !== '200')
        setOutcome(
          failed.length === 0
            ? `All ${result.results.length} done.`
            : `${result.results.length - failed.length} done, ${failed.length} refused (${failed
                .map((f) => f.status)
                .join(', ')}).`,
        )
      }
      if (body.action === 'add') setAdd('')
      void queryClient.invalidateQueries({ queryKey: ['group', jid] })
      onChanged()
    },
  })

  if (detail.isPending) return <div style={{ height: '120px' }} />
  if (detail.isError) {
    const message = detail.error instanceof ApiError ? detail.error.message : 'Could not read this group.'
    return (
      <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
        {message}
      </p>
    )
  }

  const { group, canManage, masked } = detail.data!
  const error = act.error instanceof Error ? act.error.message : null

  // Numbers are validated here as well as on the server so a typo is caught before a request
  // that would partly succeed.
  const parsed = add
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => ({ entry, phone: normalisePhone(entry).phone }))
  const unreadable = parsed.filter((p) => !p.phone)

  return (
    <div className="stack gap-4" style={{ borderTop: '1px solid var(--cad-line)', paddingTop: '16px' }}>
      {group.description && <p className="cad-note">{group.description}</p>}

      <div className="stack gap-2">
        <span className="cad-eyebrow">Members</span>
        <div className="cad-grid-frame">
          <table className="cad-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Role</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {group.participants.map((participant) => (
                <tr key={participant.jid}>
                  <td>{participant.phone}</td>
                  <td className="cad-muted">
                    {participant.admin === 'superadmin'
                      ? 'Owner'
                      : participant.admin
                        ? 'Admin'
                        : 'Member'}
                  </td>
                  {canManage && (
                    <td className="num">
                      <div className="row gap-1" style={{ justifyContent: 'flex-end' }}>
                        {participant.admin !== 'superadmin' && (
                          <button
                            type="button"
                            className="cad-btn"
                            data-variant="text"
                            disabled={act.isPending}
                            onClick={() =>
                              act.mutate({
                                action: participant.admin ? 'demote' : 'promote',
                                participants: [participant.phone],
                              })
                            }
                          >
                            {participant.admin ? 'Demote' : 'Make admin'}
                          </button>
                        )}
                        {participant.admin !== 'superadmin' && (
                          <button
                            type="button"
                            className="cad-btn"
                            data-variant="text"
                            disabled={act.isPending}
                            onClick={() => {
                              // Visible to everyone else in the group the moment it happens, and
                              // Wami cannot put them back — re-adding depends on their privacy
                              // settings, not on us.
                              const who = participant.phone
                              if (
                                confirm(
                                  `Remove ${who} from this group?\n\nEveryone in the group sees that they were removed, and whether you can add them back is up to their WhatsApp privacy settings.`,
                                )
                              ) {
                                act.mutate({ action: 'remove', participants: [participant.phone] })
                              }
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {masked && (
          <p className="cad-note">
            Numbers are hidden because your role does not include seeing them. Adding and
            removing still works by number.
          </p>
        )}
      </div>

      {canManage && (
        <>
          <div className="cad-field">
            <label htmlFor={`add-${jid}`}>Add people — one number per line</label>
            <textarea
              id={`add-${jid}`}
              className="cad-input"
              rows={3}
              value={add}
              onChange={(e) => setAdd(e.target.value)}
              placeholder={'+14155559001\n+919876543210'}
            />
            {unreadable.length > 0 && (
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                Could not read: {unreadable.map((u) => u.entry).join(', ')}
              </span>
            )}
            <div className="row gap-2" style={{ marginTop: '8px' }}>
              <button
                type="button"
                className="cad-btn"
                disabled={parsed.length === 0 || unreadable.length > 0 || act.isPending}
                onClick={() =>
                  act.mutate({
                    action: 'add',
                    participants: parsed.map((p) => p.phone!),
                  })
                }
              >
                {act.isPending ? 'Working…' : `Add ${parsed.length || ''}`.trim()}
              </button>
            </div>
          </div>

          <div className="cad-field">
            <label htmlFor={`subject-${jid}`}>Group name</label>
            <div className="row gap-2 wrap">
              <input
                id={`subject-${jid}`}
                className="cad-input"
                style={{ flex: 1, minWidth: '220px' }}
                value={subject ?? group.subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={act.isPending || (subject ?? group.subject) === group.subject}
                onClick={() => act.mutate({ action: 'subject', value: subject ?? group.subject })}
              >
                Rename
              </button>
            </div>
          </div>

          <div className="stack gap-2">
            <span className="cad-eyebrow">Who can do what</span>
            <div className="row gap-2 wrap">
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={act.isPending}
                onClick={() =>
                  act.mutate({ action: group.announceOnly ? 'not_announcement' : 'announcement' })
                }
              >
                {group.announceOnly ? 'Let everyone post' : 'Only admins may post'}
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={act.isPending}
                onClick={() => act.mutate({ action: group.locked ? 'unlocked' : 'locked' })}
              >
                {group.locked ? 'Let everyone edit details' : 'Only admins may edit details'}
              </button>
            </div>
          </div>

          <div className="stack gap-2">
            <span className="cad-eyebrow">Invite link</span>
            {link && (
              <code className="cad-bubble" style={{ wordBreak: 'break-all' }}>
                {link}
              </code>
            )}
            <div className="row gap-2 wrap">
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={act.isPending}
                onClick={() => act.mutate({ action: 'invite-link' })}
              >
                Show link
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={act.isPending}
                onClick={() => {
                  if (confirm(`Reset the invite link?\n\nEvery link already shared stops working immediately, including any posted in other chats or printed on material.`)) act.mutate({ action: 'revoke-link' })
                }}
              >
                Reset link
              </button>
            </div>
            <p className="cad-note">
              Resetting issues a new link and stops every link already shared from working.
            </p>
          </div>

          <div>
            <button
              type="button"
              className="cad-btn"
              data-variant="text"
              disabled={act.isPending}
              // Irreversible from Wami's side: rejoining needs a fresh invite from someone still
              // in the group, which Wami cannot issue for you. (Removing a participant, above,
              // is equally one-way and confirms for the same reason.)
              onClick={() => {
                const ok = confirm(
                  'Leave this group?\n\nYour number stops receiving its messages, and you can only rejoin if someone still in the group invites you back.',
                )
                if (ok) act.mutate({ action: 'leave' })
              }}
            >
              Leave this group
            </button>
          </div>
        </>
      )}

      {outcome && <p className="cad-note">{outcome}</p>}
      {error && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {error}
        </p>
      )}
    </div>
  )
}

const VERDICT_LABEL: Record<string, string> = {
  healthy: 'Working',
  thinning: 'Thinning',
  failing: 'Failing',
  dormant: 'Dormant',
}

const STATE_LABEL: Record<string, string> = {
  active: 'Posting',
  answers_only: 'Answers polls',
  silent: 'Silent',
  left: 'Left',
}

const VERDICT_TONE: Record<string, 'bad' | 'warn' | undefined> = {
  failing: 'bad',
  thinning: 'warn',
  dormant: 'warn',
}

/**
 * The one question no per-group screen answers: out of every group this member can see, which
 * ones actually need a look. Runs off the same history-only pulse `GroupInsight` computes for
 * one group at a time, just for all of them at once — a workspace with forty groups should not
 * require opening forty panels to find the three that stopped talking.
 */
function GroupHealth({ numberId }: { numberId?: string }) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const health = useQuery({ queryKey: ['groups-health'], queryFn: api.groupsHealth, retry: false })

  if (health.isPending) return null
  // Not `|| isError`: silently removing the health rollup on a failed fetch hides the very
  // signal it exists to surface, and looks identical to "every group is fine".
  if (health.isError) {
    return (
      <section className="cad-card">
        <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
          <p className="cad-muted" style={{ margin: 0 }}>Group health could not be loaded.</p>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={() => void health.refetch()}>
            Try again
          </button>
        </div>
      </section>
    )
  }
  const { groups, windowDays } = health.data!
  if (groups.length === 0) return null

  const atRisk = groups.filter((group) => group.verdict !== 'healthy')

  if (atRisk.length === 0) {
    return (
      <div className="cad-card">
        <p className="cad-muted">
          All {groups.length} group{groups.length === 1 ? '' : 's'} with history here look healthy over
          the last {windowDays} days.
        </p>
      </div>
    )
  }

  const counts = atRisk.reduce<Record<string, number>>((acc, group) => {
    acc[group.verdict] = (acc[group.verdict] ?? 0) + 1
    return acc
  }, {})

  return (
    <div className="cad-card">
      <div className="stack gap-3">
        <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
          <b>
            {atRisk.length} of {groups.length} group{groups.length === 1 ? '' : 's'} need attention
          </b>
          {(['failing', 'thinning', 'dormant'] as const)
            .filter((verdict) => counts[verdict])
            .map((verdict) => (
              <span key={verdict} className="cad-pill" data-tone={VERDICT_TONE[verdict]}>
                {counts[verdict]} {VERDICT_LABEL[verdict].toLowerCase()}
              </span>
            ))}
        </div>
        <div className="stack gap-2">
          {atRisk.map((group) => (
            <div key={group.jid} className="stack gap-1">
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                onClick={() => setExpanded(expanded === group.jid ? null : group.jid)}
              >
                <span className="cad-pill" data-tone={VERDICT_TONE[group.verdict]} style={{ marginRight: '8px' }}>
                  {VERDICT_LABEL[group.verdict] ?? group.verdict}
                </span>
                {group.subject || 'Untitled group'} — {group.reason}
              </button>
              {expanded === group.jid && <GroupInsight jid={group.jid} numberId={numberId} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * What is actually happening in this group, and the polls run in it.
 *
 * Read from our own history rather than from the live socket, which is the difference from
 * every other panel on this screen: WhatsApp can say who is in a group but not who has
 * stopped speaking in it. A group with no history here says so instead of erroring.
 */
function GroupInsight({ jid, numberId }: { jid: string; numberId?: string }) {
  const [asking, setAsking] = useState(false)

  const insight = useQuery({
    queryKey: ['group-insight', jid, numberId],
    queryFn: () => api.groupAnalytics(jid, numberId),
    retry: false,
  })

  if (insight.isPending) return <div style={{ height: '120px' }} />
  if (insight.isError) {
    const message =
      insight.error instanceof ApiError ? insight.error.message : 'Could not read this group.'
    return <p className="cad-note">{message}</p>
  }

  const { conversationId, windowDays, rosterKnown, participants, pulse } = insight.data!

  return (
    <div className="stack gap-4" style={{ borderTop: '1px solid var(--cad-line)', paddingTop: '16px' }}>
      <div className="stack gap-2">
        <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
          <span className="cad-eyebrow">Last {windowDays} days</span>
          <span className="cad-pill">{VERDICT_LABEL[pulse.verdict] ?? pulse.verdict}</span>
        </div>
        <p className="cad-note">{pulse.reason}</p>
        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
          {pulse.messages} messages · {pulse.speakersRecent} people talking now, {pulse.speakersEarlier}{' '}
          earlier in the window · {pulse.silent} of {pulse.members} have said nothing
          {pulse.answerRate === null ? '' : ` · ${Math.round(pulse.answerRate * 100)}% of polls answered`}
        </span>
        {!rosterKnown && (
          <p className="cad-note">
            The live group could not be read, so this counts only the people who appear in its
            history here. Members who have never posted are not in this list.
          </p>
        )}
      </div>

      <div className="stack gap-2">
        <span className="cad-eyebrow">People</span>
        <div className="cad-grid-frame">
          <table className="cad-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>State</th>
                <th className="num">Messages</th>
                <th className="num">Polls answered</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((person) => (
                <tr key={person.phone ?? 'unknown'}>
                  <td>
                    {person.phone}
                    {person.admin && <span className="cad-muted"> · admin</span>}
                  </td>
                  <td className="cad-muted">{STATE_LABEL[person.state] ?? person.state}</td>
                  <td className="num">{person.messages}</td>
                  <td className="num">
                    {person.pollsAsked === 0
                      ? // Never asked and never answers are opposite problems, so they must
                        // not read the same.
                        '—'
                      : `${person.pollsAnswered} of ${person.pollsAsked}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <GroupPolls conversationId={conversationId} asking={asking} setAsking={setAsking} />
    </div>
  )
}

function GroupPolls({
  conversationId,
  asking,
  setAsking,
}: {
  conversationId: string
  asking: boolean
  setAsking: (value: boolean) => void
}) {
  const queryClient = useQueryClient()
  const list = useQuery({
    queryKey: ['polls', conversationId],
    queryFn: () => api.polls(conversationId),
    retry: false,
  })

  const close = useMutation({
    mutationFn: (id: string) => api.closePoll(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['polls', conversationId] }),
  })

  if (list.isPending) return <div style={{ height: '80px' }} />
  if (list.isError) {
    return (
      <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
        <p className="cad-muted" style={{ margin: 0 }}>Polls could not be loaded.</p>
        <button type="button" className="cad-btn" data-variant="quiet" onClick={() => void list.refetch()}>
          Try again
        </button>
      </div>
    )
  }

  const { polls, canManage } = list.data!

  return (
    <div className="stack gap-3">
      <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
        <span className="cad-eyebrow">Polls</span>
        {canManage && !asking && (
          <button type="button" className="cad-btn" data-variant="quiet" onClick={() => setAsking(true)}>
            Ask a poll
          </button>
        )}
      </div>

      {asking && (
        <NewPoll
          conversationId={conversationId}
          onCancel={() => setAsking(false)}
          onAsked={() => {
            setAsking(false)
            void queryClient.invalidateQueries({ queryKey: ['polls', conversationId] })
          }}
        />
      )}

      {polls.length === 0 && !asking && (
        <p className="cad-muted">
          No polls yet. A poll is the fastest way to get an answer out of a group that does not
          reply to messages.
        </p>
      )}

      {close.error && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {close.error instanceof Error ? close.error.message : 'Could not stop chasing that poll.'}
        </p>
      )}

      {polls.map((poll) => (
        <PollResult
          key={poll.id}
          poll={poll}
          canManage={canManage}
          closing={close.isPending}
          onClose={() => close.mutate(poll.id)}
        />
      ))}
    </div>
  )
}

function PollResult({
  poll,
  canManage,
  closing,
  onClose,
}: {
  poll: PollView
  canManage: boolean
  closing: boolean
  onClose: () => void
}) {
  const { results } = poll

  return (
    <div className="cad-bubble stack gap-2">
      <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
        <b>{poll.question}</b>
        {canManage && poll.closedAt === null && (
          <button type="button" className="cad-btn" data-variant="text" disabled={closing} onClick={onClose}>
            {closing ? 'Stopping…' : 'Stop chasing'}
          </button>
        )}
      </div>
      <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{results.sentence}</span>

      <div className="stack gap-1">
        {results.options.map((option) => (
          <div key={option.option} className="row between gap-2" style={{ alignItems: 'center' }}>
            <span>{option.option}</span>
            <span className="cad-muted">
              {option.votes}
              {results.voters > 0 && ` · ${Math.round(option.share * 100)}%`}
            </span>
          </div>
        ))}
      </div>

      {results.participation.silent.length > 0 && (
        <p className="cad-note">
          Not answered by {results.participation.silent.join(', ')}
        </p>
      )}
      {poll.closedAt !== null && (
        // Local only: WhatsApp has no way to close a poll, and votes cast after this are still
        // recorded. Saying otherwise would be a lie about what happened on the wire.
        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
          Marked as done here. People can still vote in WhatsApp, and late votes still count.
        </span>
      )}
    </div>
  )
}

function NewPoll({
  conversationId,
  onCancel,
  onAsked,
}: {
  conversationId: string
  onCancel: () => void
  onAsked: () => void
}) {
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState('')
  const [multi, setMulti] = useState(false)

  const lines = options
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  // Validated with the same function the server uses, so a poll that cannot be counted is
  // refused while it is still being typed rather than after it has reached a group.
  const checked = normalisePoll({ question, options: lines, selectableCount: multi ? 0 : 1 })

  const ask = useMutation({
    mutationFn: () =>
      api.createPoll({
        conversationId,
        question: checked.poll!.question,
        options: checked.poll!.options,
        selectableCount: checked.poll!.selectableCount,
      }),
    onSuccess: onAsked,
  })

  return (
    <div className="cad-bubble stack gap-3">
      <div className="cad-field">
        <label htmlFor="poll-question">Question</label>
        <input
          id="poll-question"
          className="cad-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Which shift can you take tomorrow?"
        />
      </div>
      <div className="cad-field">
        <label htmlFor="poll-options">Answers — one per line</label>
        <textarea
          id="poll-options"
          className="cad-input"
          rows={4}
          value={options}
          onChange={(e) => setOptions(e.target.value)}
          placeholder={'Morning\nAfternoon\nNight\nNot available'}
        />
      </div>
      <label className="row gap-2" style={{ alignItems: 'center' }}>
        <input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} />
        <span>People can pick more than one</span>
      </label>

      {question.trim() !== '' && lines.length > 0 && checked.error && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{checked.error}</p>
      )}
      {ask.error && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {ask.error instanceof Error ? ask.error.message : 'That did not work.'}
        </p>
      )}

      <div className="row gap-2 wrap">
        <button
          type="button"
          className="cad-btn"
          disabled={checked.poll === null || ask.isPending}
          onClick={() => ask.mutate()}
        >
          {ask.isPending ? 'Sending…' : 'Send poll'}
        </button>
        <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

function CreateGroup({
  numberId,
  onCancel,
  onCreated,
}: {
  numberId?: string
  onCancel: () => void
  onCreated: () => void
}) {
  const [subject, setSubject] = useState('')
  const [members, setMembers] = useState('')
  const [blueprintId, setBlueprintId] = useState('')

  const blueprints = useQuery({
    queryKey: ['group-blueprints'],
    queryFn: () => api.groupBlueprints(),
    retry: false,
  })
  const chosen: GroupBlueprint | null =
    blueprints.data?.blueprints.find((b) => b.id === blueprintId) ?? null

  const parsed = members
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => ({ entry, phone: normalisePhone(entry).phone }))
  const unreadable = parsed.filter((p) => !p.phone)

  const create = useMutation({
    mutationFn: () =>
      api.createGroup({
        subject: subject.trim(),
        participants: parsed.map((p) => p.phone!),
        numberId,
        ...(blueprintId ? { blueprintId } : {}),
      }),
    onSuccess: onCreated,
  })

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <h2 className="cad-section-title">New group</h2>
        {(blueprints.data?.blueprints.length ?? 0) > 0 && (
          <div className="cad-field">
            <label htmlFor="new-group-blueprint">Start from</label>
            <select
              id="new-group-blueprint"
              className="cad-input"
              value={blueprintId}
              onChange={(e) => {
                setBlueprintId(e.target.value)
                const picked = blueprints.data?.blueprints.find((b) => b.id === e.target.value)
                // Only fills a name the operator has not written; a blueprint chosen second
                // must not overwrite what they typed first.
                if (picked && !subject.trim()) setSubject(picked.subject)
              }}
            >
              <option value="">Nothing — an empty group</option>
              {blueprints.data!.blueprints.map((blueprint) => (
                <option key={blueprint.id} value={blueprint.id}>
                  {blueprint.name}
                </option>
              ))}
            </select>
            {chosen && (
              <>
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                  {chosen.purpose}
                  {chosen.announceOnly && ' Only admins will be able to post.'}
                </span>
                {chosen.caution && (
                  // A group shows every member's number to every other member. In healthcare
                  // and financial services that alone can be the breach, and no setting fixes
                  // it — so it is said before the group exists, not after.
                  <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                    {chosen.caution}
                  </p>
                )}
                {chosen.polls.length > 0 && (
                  <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                    Suggests {chosen.polls.length}{' '}
                    {chosen.polls.length === 1 ? 'poll' : 'polls'} to ask once it exists.
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <div className="cad-field">
          <label htmlFor="new-group-subject">Name</label>
          <input
            id="new-group-subject"
            className="cad-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Kolkata delivery team"
          />
        </div>
        <div className="cad-field">
          <label htmlFor="new-group-members">Members — one number per line</label>
          <textarea
            id="new-group-members"
            className="cad-input"
            rows={4}
            value={members}
            onChange={(e) => setMembers(e.target.value)}
            placeholder={'+14155559001\n+919876543210'}
          />
          {unreadable.length > 0 && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              Could not read: {unreadable.map((u) => u.entry).join(', ')}
            </span>
          )}
        </div>
        {create.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {create.error instanceof Error ? create.error.message : 'That did not work.'}
          </p>
        )}
        <div className="row gap-2 wrap">
          <button
            type="button"
            className="cad-btn"
            disabled={!subject.trim() || parsed.length === 0 || unreadable.length > 0 || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create group'}
          </button>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  )
}
